// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { LocationModel } from "@insite/client-framework/Common/Hooks/useLocationFilterSearch";
import {
    CurrentLocationInfoWindow,
    LocationGoogleMapMarkerType,
    LocationGoogleMapsMarker,
    LocationInfoWindow,
} from "@insite/client-framework/Common/Hooks/useLocationGoogleMarkers";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { newGuid } from "@insite/client-framework/Common/StringHelpers";
import XngWithDynamicGoogleMaps from "@stoneage/App/Components/Common/XngWithDynamicGoogleMaps";
import { AddressInfoCondensedDisplayStyles } from "@insite/content-library/Components/AddressInfoCondensedDisplay";
import { DistanceUnitOfMeasure } from "@insite/content-library/Components/DistanceDisplay";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import { GoogleMapProps, InfoWindowProps, MarkerProps } from "@react-google-maps/api";
import GoogleMapLegend from "@stoneage/App/Components/Locations/GoogleMapLegend";
// XNG-Changes
import LocationInfoWindowCard, {
    LocationInfoWindowCardStyles,
} from "@stoneage/App/Components/Locations/LocationInfoWindowCard";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import * as React from "react";
import { css } from "styled-components";
import { XLocationModel } from "@stoneage/Overrides/Components/LocationSearchForm";

interface Props {
    extendedStyles?: LocationGoogleMapStyles;
    currentLocation?: google.maps.LatLng;
    locationSearchFilter: string;
    distanceUnitOfMeasure: DistanceUnitOfMeasure;
    mapMarkerElements: LocationGoogleMapsMarker[];
    // locationInfoWindow?: LocationInfoWindow<LocationModel>;
    currentLocationInfoWindow?: CurrentLocationInfoWindow;
    handleOpenLocationContent: (location: LocationModel) => void;
    setGoogleMap: (map: google.maps.Map | undefined) => void;
    GoogleMap: React.ComponentType<GoogleMapProps>;
    InfoWindow: React.ComponentType<InfoWindowProps>;
    Marker: React.ComponentType<MarkerProps>;
    // XNG-Changes: navigates to Dealer Details (e.g. history.push) - the hand-off to
    // xUpdateSearchTextField below is what lets LocationFinder restore this search on return
    onLocationSelected?: (location: XLocationModel) => void;
    xUpdateSearchTextField?: (parameter: { locationSearchText?: string; searchRadius?: number }) => void;
    geoLocationSearchText?: string;
    radiusSearchFilter?: number;
    locationInfoWindow?: LocationInfoWindow<LocationModel & { properties: { [key: string]: string } }>; // Add properties object
    distanceTitleOverride?: string;
    legendList?: {
        imgLink: string;
        label: string;
    }[];
}

export interface LocationGoogleMapStyles {
    googleMap?: GoogleMapProps;
    locationMarker?: MarkerProps;
    currentLocationMarker?: MarkerProps;
    selectedMarker?: MarkerProps;
    locationInfoWindow?: InfoWindowProps;
    currentLocationInfoWindow?: InfoWindowProps;
    infoWindowGridContainer?: GridContainerProps;
    infoWindowLeftGridItem?: GridItemProps;
    selectedLocationName?: TypographyPresentationProps;
    selectedLocationAddressDisplay?: AddressInfoCondensedDisplayStyles;
    infoWindowRightGridItem?: GridItemProps;
    infoWindowLocationContentLink?: LinkPresentationProps;
    infoWindowGoogleMapsDirectionLink?: LinkPresentationProps;
    infoWindowDistanceDisplay?: TypographyPresentationProps;
    currentLocationInfoWindowGridContainer?: GridContainerProps;
    currentLocationInfoWindowHeaderGridItem?: GridItemProps;
    currentLocationInfoWindowHeaderText?: TypographyPresentationProps;
    currentLocationInfoWindowSearchFilterGridItem?: GridItemProps;
    currentLocationInfoWindowSearchFilterText?: TypographyPresentationProps;
    // XNG-Changes
    locationInfoWindowCard?: LocationInfoWindowCardStyles;
    currentLocationInfoWindowCard?: LocationInfoWindowCardStyles;
}

export const locationGoogleMapStyles: LocationGoogleMapStyles = {
    googleMap: {
        zoom: 14,
        mapContainerStyle: {
            height: "642px",
            width: "100%",
        },
    },
    infoWindowGridContainer: {
        gap: 0,
    },
    infoWindowLeftGridItem: {
        width: 8,
        css: css`
            flex-direction: column;
        `,
    },
    infoWindowRightGridItem: {
        width: 4,
        css: css`
            flex-direction: column;
        `,
    },
    selectedLocationName: {
        color: "primary",
        weight: "bold",
    },
    currentLocationInfoWindowGridContainer: {
        gap: 0,
    },
    currentLocationInfoWindowHeaderGridItem: {
        width: 12,
        css: css`
            justify-content: center;
        `,
    },
    currentLocationInfoWindowSearchFilterGridItem: {
        width: 12,
        css: css`
            justify-content: center;
        `,
    },
};

const LocationGoogleMap: React.FC<Props> = ({
    extendedStyles,
    currentLocation,
    locationSearchFilter,
    distanceUnitOfMeasure,
    setGoogleMap,
    mapMarkerElements,
    locationInfoWindow,
    currentLocationInfoWindow,
    onLocationSelected,
    xUpdateSearchTextField,
    geoLocationSearchText,
    radiusSearchFilter,
    GoogleMap,
    InfoWindow,
    Marker,
    // XNG-Changes
    distanceTitleOverride,
    legendList,
}) => {
    const [styles] = React.useState(() => mergeToNew(locationGoogleMapStyles, extendedStyles));
    // XNG-Changes
    const dealerDetailsUrl = useAppSelector(state => getPageLinkByPageType(state, "DealerDetailsPage")?.url);

    // XNG-Changes: mirrors LocationSearchForm's handleLocationClick, so opening Dealer Details
    // from the map info window hands off the current search the same way the results list does
    const handleLocationClick = React.useCallback(
        (event: React.MouseEvent<Element, MouseEvent>, location: XLocationModel) => {
            event.preventDefault();
            onLocationSelected?.(location);
            xUpdateSearchTextField?.({
                locationSearchText: geoLocationSearchText,
                searchRadius: radiusSearchFilter,
            });
        },
        [onLocationSelected, xUpdateSearchTextField, geoLocationSearchText, radiusSearchFilter],
    );

    const handleLoad = (map: google.maps.Map) => {
        setGoogleMap(map);
    };
    const handleUnmount = (_: google.maps.Map) => {
        setGoogleMap(undefined);
    };

    const getMarkerStyles = (markerType: LocationGoogleMapMarkerType) => {
        if (markerType === "CURRENT_LOCATION") {
            return styles.currentLocationMarker;
        }
        if (markerType === "SELECTED") {
            return styles.selectedMarker;
        }
        return styles.locationMarker;
    };

    return (
        <GoogleMap {...styles.googleMap} center={currentLocation} onLoad={handleLoad} onUnmount={handleUnmount}>
            {/* XNG-Changes: added GoogleMapLegend  */}
            <GoogleMapLegend legendsList={legendList} />
            {mapMarkerElements.map(marker => (
                <Marker {...marker} key={marker.key} {...getMarkerStyles(marker.type)} />
            ))}
            {/* XNG-Changes:Change structure */}
            {locationInfoWindow && (
                <InfoWindow
                    options={{
                        headerDisabled: true,
                    }}
                    {...styles.locationInfoWindow}
                    key={newGuid()}
                    position={locationInfoWindow?.position}
                >
                    {/* OOTB code, replaced by LocationInfoWindowCard below
                    {locationInfoWindow && (
                        <GridContainer {...styles.infoWindowGridContainer}>
                            <GridItem {...styles.infoWindowLeftGridItem}>
                                <Typography {...styles.selectedLocationName}>
                                    {locationInfoWindow.location.name}
                                </Typography>
                                <AddressInfoCondensedDisplay
                                    {...locationInfoWindow.location}
                                    extendedStyles={styles.selectedLocationAddressDisplay}
                                />
                            </GridItem>
                            <GridItem {...styles.infoWindowRightGridItem}>
                                {locationInfoWindow.location.htmlContent && (
                                    <LocationContentLink
                                        {...styles.infoWindowLocationContentLink}
                                        location={locationInfoWindow.location}
                                        onOpenLocationContent={handleOpenLocationContent}
                                    />
                                )}
                                <GoogleMapsDirectionLink
                                    {...styles.infoWindowGoogleMapsDirectionLink}
                                    {...locationInfoWindow.location}
                                />
                                {locationInfoWindow.location.distance > 0.01 && (
                                    <DistanceDisplay
                                        {...styles.infoWindowDistanceDisplay}
                                        distance={locationInfoWindow.location.distance}
                                        unitOfMeasure={distanceUnitOfMeasure}
                                    />
                                )}
                            </GridItem>
                        </GridContainer>
                    )}
                    */}
                    {/* XNG-Changes */}
                    <LocationInfoWindowCard
                        location={locationInfoWindow.location}
                        distanceUnitOfMeasure={distanceUnitOfMeasure}
                        distanceTitleOverride={distanceTitleOverride}
                        dealerDetailsUrl={dealerDetailsUrl}
                        extendedStyles={styles.locationInfoWindowCard}
                        onLocationSelected={handleLocationClick}
                    />
                </InfoWindow>
            )}
            {currentLocationInfoWindow && (
                <InfoWindow
                    {...styles.currentLocationInfoWindow}
                    options={{
                        headerDisabled: true,
                    }}
                    key={newGuid()}
                    position={currentLocationInfoWindow.position}
                >
                    {/* OOTB code, replaced by CurrentLocationInfoWindowCard below
                    <GridContainer {...styles.currentLocationInfoWindowGridContainer}>
                        <GridItem {...styles.currentLocationInfoWindowHeaderGridItem}>
                            <Typography {...styles.currentLocationInfoWindowHeaderText}>
                                {translate("Current Location")}
                            </Typography>
                        </GridItem>
                        <GridItem {...styles.currentLocationInfoWindowSearchFilterGridItem}>
                            <Typography {...styles.currentLocationInfoWindowSearchFilterText}>
                                {locationSearchFilter}
                            </Typography>
                        </GridItem>
                    </GridContainer>
                    */}
                    {/* XNG-Changes */}
                    <LocationInfoWindowCard
                        isCurrentLocation={true}
                        searchFilter={locationSearchFilter}
                        extendedStyles={styles.currentLocationInfoWindowCard}
                    />
                </InfoWindow>
            )}
        </GoogleMap>
    );
};

export default XngWithDynamicGoogleMaps(LocationGoogleMap);
